import { useState, useCallback, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  aiScoreTestApi,
  TestScoreStatusResponse,
  Session,
} from '../api/aiScoreTest.api';
import { useShowToast } from '@/hooks';

// Constants
const MAX_POLLING_RETRIES = 25;
const POLLING_INTERVAL_MS = 2000;

interface UseAiScoreTestProps {
  jobId: string;
}

interface TestState {
  isTestMode: boolean;
  session: Session | null;
  resultKey: string | null;
  scoreData: TestScoreStatusResponse | null;
  isLoading: boolean; // Add loading state to test state
}

export const useAiScoreTest = ({ jobId }: UseAiScoreTestProps) => {
  const [testState, setTestState] = useState<TestState>({
    isTestMode: true,
    session: null,
    resultKey: null,
    scoreData: null,
    isLoading: false,
  });

  const toast = useShowToast();
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingRetryCountRef = useRef<number>(0);

  // Clear polling timeout
  const clearPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    pollingRetryCountRef.current = 0;
  }, []);

  // Poll for test score status
  const pollTestScoreStatus = useCallback(
    async (resultKey: string) => {
      try {
        // Check if we've exceeded max retries
        if (pollingRetryCountRef.current >= MAX_POLLING_RETRIES) {
          // Product copy (ENG-3832). Replaces "Test timeout", which named an
          // internal limit rather than telling the user what to do.
          toast({
            title:
              'This resume is taking too long to process. Try a different one.',
            status: 'error',
          });
          setTestState((prev) => ({
            ...prev,
            isTestMode: true,
            scoreData: null,
            isLoading: false,
          }));
          clearPolling();
          return;
        }

        pollingRetryCountRef.current += 1;

        const { data, error } = await aiScoreTestApi.getTestScoreStatus(
          jobId,
          resultKey
        );

        if (error) {
          console.error('Error polling test score status:', error);
          toast({
            title: 'Error getting test results',
            status: 'error',
            description:
              'Failed to get test scoring results. Please try again.',
          });
          setTestState((prev) => ({
            ...prev,
            isLoading: false,
          }));
          clearPolling();
          return;
        }

        if (data) {
          // Both pending and completed responses have the same structure: { data: { status: "...", ... }, errors: [] }
          if ('data' in data && data.data && 'status' in data.data) {
            const { status } = data.data;

            if (status === 'pending') {
              // Continue polling - keep loading state true
              pollingTimeoutRef.current = setTimeout(() => {
                // `pollTestScoreStatus` is declared below but only invoked after mount, so
                // the binding is initialised by the time this runs.
                // eslint-disable-next-line react-hooks/immutability
                pollTestScoreStatus(resultKey);
              }, POLLING_INTERVAL_MS);
            } else if (status === 'completed') {
              // Test completed successfully
              setTestState((prev) => ({
                ...prev,
                isTestMode: false,
                scoreData: data, // Keep the full response structure for compatibility
                isLoading: false,
              }));
              clearPolling();
            } else if (status === 'failed') {
              // Test failed
              const errorMessage =
                'error' in data.data
                  ? data.data.error
                  : 'The AI scoring test failed. Please try again.';
              toast({
                title: 'Test scoring failed',
                status: 'error',
                description:
                  errorMessage ||
                  'The AI scoring test failed. Please try again.',
              });
              setTestState((prev) => ({
                ...prev,
                isTestMode: true,
                scoreData: null,
                isLoading: false,
              }));
              clearPolling();
            }
          }
        }
      } catch (error) {
        console.error('Error in pollTestScoreStatus:', error);
        toast({
          title: 'Error getting test results',
          status: 'error',
          description: 'Failed to get test scoring results. Please try again.',
        });
        setTestState((prev) => ({
          ...prev,
          isLoading: false,
        }));
        clearPolling();
      }
    },
    [jobId, toast, clearPolling]
  );

  // Start test score mutation
  const startTestMutation = useMutation({
    mutationFn: async (payload: {
      contact_id?: number;
      resume_file?: File;
      result_key?: string;
    }) => {
      return aiScoreTestApi.testScore(jobId, payload);
    },
    onMutate: () => {
      // Start loading immediately when mutation begins
      setTestState((prev) => ({
        ...prev,
        isLoading: true,
      }));
      // Reset retry counter
      pollingRetryCountRef.current = 0;
    },
    onSuccess: (response) => {
      const { data, error } = response;

      if (error) {
        console.error('Error starting test score:', error);
        toast({
          title: 'Error starting test',
          status: 'error',
          description: 'Failed to start AI scoring test. Please try again.',
        });
        setTestState((prev) => ({
          ...prev,
          isLoading: false,
        }));
        return;
      }

      if (data) {
        const { result_key, session } = data.data; // Access from data.data since that's where session is nested

        setTestState((prev) => ({
          ...prev,
          resultKey: result_key,
          session: session || null,
          // Keep loading state true - polling will handle setting it to false
        }));

        // Start polling for results
        pollTestScoreStatus(result_key);
      }
    },
    onError: (error) => {
      console.error('Test score mutation error:', error);
      toast({
        title: 'Error starting test',
        status: 'error',
        description: 'Failed to start AI scoring test. Please try again.',
      });
      setTestState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    },
  });

  // Delete session mutation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const deleteSessionMutation = useMutation({
    mutationFn: async (resultKey: string) => {
      return aiScoreTestApi.deleteTestSession(jobId, resultKey);
    },
    onSuccess: () => {
      // Session deleted successfully
      console.log('🗑️ Session deleted successfully');
      setTestState({
        isTestMode: true,
        session: null,
        resultKey: null,
        scoreData: null,
        isLoading: false,
      });
      clearPolling();
    },
    onError: (error) => {
      console.error('Error deleting session:', error);
      // Don't show error toast for session deletion as it's not critical
    },
  });

  // Start test with resume file
  const startTestWithResume = useCallback(
    (resumeFile: File) => {
      startTestMutation.mutate({ resume_file: resumeFile });
    },
    // Mutation objects are read inside the callbacks only; see note above.
    // eslint-disable-next-line @tanstack/query/no-unstable-deps
    [startTestMutation]
  );

  // Start test with existing contact
  const startTestWithContact = useCallback(
    (contactId: number) => {
      startTestMutation.mutate({ contact_id: contactId });
    },
    // eslint-disable-next-line @tanstack/query/no-unstable-deps
    [startTestMutation]
  );

  // Retest with existing session
  const retestWithSession = useCallback(() => {
    if (testState.resultKey) {
      startTestMutation.mutate({ result_key: testState.resultKey });
    }
    // eslint-disable-next-line @tanstack/query/no-unstable-deps
  }, [startTestMutation, testState.resultKey]);

  const testAgainNewCandidate = useCallback(() => {
    setTestState({
      isTestMode: true,
      session: null,
      resultKey: null,
      scoreData: null,
      isLoading: false,
    });
  }, []);

  // Reset to test mode (used when attributes change)
  const resetToTestMode = useCallback(() => {
    setTestState((prev) => ({
      ...prev,
      isTestMode: true,
      scoreData: null,
    }));
  }, []);

  return {
    // State
    isTestMode: testState.isTestMode,
    isLoading: testState.isLoading,
    session: testState.session,
    scoreData: testState.scoreData,
    hasSession: !!testState.resultKey,
    resultKey: testState.resultKey,

    // Actions
    startTestWithResume,
    startTestWithContact,
    retestWithSession,
    testAgainNewCandidate,
    resetToTestMode,

    // Cleanup
    clearPolling,
  };
};
